<template><div><h2 id="font-properties" tabindex="-1" class="d-docsite--header-2">Font Properties <a class="header-anchor d-link d-docsite--link d-link" href="#font-properties" aria-hidden="true">#</a></h2>
<h3 id="font-family" tabindex="-1" class="d-docsite--header-3">Font Family <a class="header-anchor d-link d-docsite--link d-link" href="#font-family" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Dialtone's product UI font stack defers to the user's default system font, ensuring a seamless native experience.</p>
<dt-stack direction="row" gap="200" class="d-wmx764">
  <svg-loader class="d-fl1" name="ff-appleSF" />
  <svg-loader class="d-fl1" name="ff-windows" />
  <svg-loader class="d-fl1" name="ff-roboto" />
  <svg-loader class="d-fl1" name="ff-linux" />
</dt-stack>
<h3 id="font-weight" tabindex="-1" class="d-docsite--header-3">Font Weight <a class="header-anchor d-link d-docsite--link d-link" href="#font-weight" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Four weights for clear hierarchy and visual contrast among different elements.</p>
<dt-stack direction="row" gap="200" class="d-wmx764">
  <svg-loader name="fw-regular" />
  <svg-loader name="fw-medium" />
  <svg-loader name="fw-semibold" />
  <svg-loader name="fw-bold" />
</dt-stack>
<h3 id="font-size" tabindex="-1" class="d-docsite--header-3">Font Size <a class="header-anchor d-link d-docsite--link d-link" href="#font-size" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Dictates the scale of text, enhancing readability and defining information hierarchy across content.</p>
<dt-stack gap="200">
  <dt-stack direction="row" gap="200">
    <svg-loader name="fs12" style="max-width: 140px" /> <!--lazy inline style until we redesign this whole page -->
    <svg-loader name="fs14" style="max-width: 140px" />
    <svg-loader name="fs16" style="max-width: 140px" />
    <svg-loader name="fs18" style="max-width: 140px" />
    <svg-loader name="fs20" style="max-width: 140px" />
  </dt-stack>
  <dt-stack direction="row" gap="200">
    <svg-loader name="fs28" style="max-width: 140px" />
    <svg-loader name="fs32" style="max-width: 140px" />
  </dt-stack>
</dt-stack>
<h3 id="line-height" tabindex="-1" class="d-docsite--header-3">Line Height <a class="header-anchor d-link d-docsite--link d-link" href="#line-height" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Adjusts vertical spacing between lines of text, optimizing legibility and text flow.</p>
<dt-stack direction="column" gap="200">
  <dt-stack direction="row" gap="200" class="d-wmx764">
    <svg-loader name="lh-100" />
    <svg-loader name="lh-200" />
    <svg-loader name="lh-300" />
  </dt-stack>
  <dt-stack direction="row" gap="200" class="d-wmx764">
    <svg-loader name="lh-400" />
    <svg-loader name="lh-500" />
    <svg-loader name="lh-600" />
  </dt-stack>
</dt-stack>
<h2 id="styles" tabindex="-1" class="d-docsite--header-2">Styles <a class="header-anchor d-link d-docsite--link d-link" href="#styles" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Product UI text can be characterized as one of <strong>Headline</strong>, <strong>Body</strong>, <strong>Label</strong>, or <strong>Code</strong>.</p>
<h3 id="vue-component" tabindex="-1" class="d-docsite--header-3">Vue Component <a class="header-anchor d-link d-docsite--link d-link" href="#vue-component" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <a href="/components/text.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtText</a> component as the primary way to apply typography styles. The component provides a semantic, prop-driven API that's easier to maintain and ensures consistent usage across your application.</p>
<code-example only-show="code" source-code='&lt;dt-text kind=&quot;headline|body|label|code&quot; size=&quot;{size}&quot; {{props}}&gt;...&lt;/dt-text&gt;'>
<dt-text kind="headline|body|label|code" size="{size}" {{props}}>...</dt-text>
</code-example>
<h3 id="css-utility" tabindex="-1" class="d-docsite--header-3">CSS Utility <a class="header-anchor d-link d-docsite--link d-link" href="#css-utility" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Text Style CSS utilities should be considered a last resort or as a fallback for non-Vue contexts.</p>
<code-example only-show="code" source-code='&lt;el class=&quot;d-text-{category}--{size}&quot;&gt;...&lt;/el&gt;'>
<el class="d-text-{category}--{size}">...</el>
</code-example>
<h3 id="headlines" tabindex="-1" class="d-docsite--header-3">Headlines <a class="header-anchor d-link d-docsite--link d-link" href="#headlines" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Titles and headings to establish hierarchy and set the tone of contextual groupings.</p>
<code-well-header class="d-p-200 d-bgc-secondary" custom>
  <table class="d-w100p">
    <tbody>
      <template v-for="({ var: varName }, index) in typographyStylesHeadlines" :key="varName">
        <tr>
          <td class="d-p-50 d-lc-1">
            <div :class="[varName]">{{ example }}</div>
          </td>
          <td class="d-p-50 d-ws-nowrap">
            <dt-stack direction="row" as="code" gap="50" class="d-text-code--xs d-bgc-transparent d-ta-center d-c-default">
              <span v-dt-tooltip="`Font Size`">{{ getStyles('headlines', index).fontSize }}</span>
              /
              <span v-dt-tooltip="`Line Height`">{{ getStyles('headlines', index).lineHeight }}</span>
              /
              <span v-dt-tooltip="`Font Weight`">{{ getStyles('headlines', index).fontWeightName }}</span>
            </dt-stack>
          </td>
          <td class="d-p-50 d-ws-nowrap">
            <dt-stack direction="row" justify="between" class="dialtone-copy-utility">
              <code class="dialtone-copy-utility__utility d-bgc-transparent d-text-code--xs">{{ varName }}</code>
              <div class="dialtone-copy-utility__btn">
                <copy-button :text="varName" aria-label="Copy" />
              </div>
            </dt-stack>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</code-well-header>
<h3 id="body" tabindex="-1" class="d-docsite--header-3">Body <a class="header-anchor d-link d-docsite--link d-link" href="#body" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Default text style for longer-form prose content, designed for comfort and clarity in reading varying lengths.</p>
<code-well-header class="d-p-200 d-bgc-secondary" custom>
  <table class="d-w100p">
    <tbody>
      <tr v-for="({ var: varName }, index) in typographyStylesBody" :key="varName">
        <td class="d-p-50 d-lc-1">
          <div :class="[varName]">{{ example }}</div>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" as="code" gap="50" class="d-text-code--xs d-bgc-transparent d-ta-center d-c-default">
            <span v-dt-tooltip="`Font Size`">{{ getStyles('body', index).fontSize }}</span>
            /
            <span v-dt-tooltip="`Line Height`">{{ getStyles('body', index).lineHeight }}</span>
            /
            <span v-dt-tooltip="`Font Weight`">{{ getStyles('body', index).fontWeightName }}</span>
          </dt-stack>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" justify="between" class="dialtone-copy-utility">
            <code class="dialtone-copy-utility__utility d-bgc-transparent d-text-code--xs">{{ varName }}</code>
            <div class="dialtone-copy-utility__btn">
              <copy-button :text="varName" aria-label="Copy" />
            </div>
          </dt-stack>
        </td>
      </tr>
    </tbody>
  </table>
</code-well-header>
<h3 id="label" tabindex="-1" class="d-docsite--header-3">Label <a class="header-anchor d-link d-docsite--link d-link" href="#label" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Shorter-length copy like form fields, buttons, and other UI-labeling elements, ensuring clear navigation and
interaction.</p>
<code-well-header class="d-p-200 d-bgc-secondary" custom>
  <table class="d-w100p">
    <tbody>
      <tr v-for="({ var: varName }, index) in typographyStylesLabel" :key="varName">
        <td class="d-p-50 d-lc-1">
          <div :class="[varName]">{{ example }}</div>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" as="code" gap="50" class="d-text-code--xs d-bgc-transparent d-ta-center d-c-default">
            <span v-dt-tooltip="`Font Size`">{{ getStyles('label', index).fontSize }}</span>
            /
            <span v-dt-tooltip="`Line Height`">{{ getStyles('label', index).lineHeight }}</span>
            /
            <span v-dt-tooltip="`Font Weight`">{{ getStyles('label', index).fontWeightName }}</span>
          </dt-stack>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" justify="between" class="dialtone-copy-utility">
            <code class="dialtone-copy-utility__utility d-bgc-transparent d-text-code--xs">{{ varName }}</code>
            <div class="dialtone-copy-utility__btn">
              <copy-button :text="varName" aria-label="Copy" />
            </div>
          </dt-stack>
        </td>
      </tr>
    </tbody>
  </table>
</code-well-header>
<h3 id="code" tabindex="-1" class="d-docsite--header-3">Code <a class="header-anchor d-link d-docsite--link d-link" href="#code" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Code snippets, technical commands, or data values rendered as a monospaced font.</p>
<code-well-header class="d-p-200 d-bgc-secondary" custom>
  <table class=d-w100p>
    <tbody>
      <tr v-for="({ var: varName }, index) in typographyStylesCode" :key="varName">
        <td class="d-p-50 d-lc-1">
          <div :class="[varName]">{{ example }}</div>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" as="code" gap="50" class="d-text-code--xs d-bgc-transparent d-ta-center d-c-default">
            <span v-dt-tooltip="`Font Size`">{{ getStyles('code', index).fontSize }}</span>
            /
            <span v-dt-tooltip="`Line Height`">{{ getStyles('code', index).lineHeight }}</span>
            /
            <span v-dt-tooltip="`Font Weight`">{{ getStyles('code', index).fontWeightName }}</span>
          </dt-stack>
        </td>
        <td class="d-p-50 d-ws-nowrap">
          <dt-stack direction="row" justify="between" class="dialtone-copy-utility">
            <code class="dialtone-copy-utility__utility d-bgc-transparent d-text-code--xs">{{ varName }}</code>
            <div class="dialtone-copy-utility__btn">
              <copy-button :text="varName" aria-label="Copy" />
            </div>
          </dt-stack>
        </td>
      </tr>
    </tbody>
  </table>
</code-well-header>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="profile-card" tabindex="-1" class="d-docsite--header-3">Profile Card <a class="header-anchor d-link d-docsite--link d-link" href="#profile-card" aria-hidden="true">#</a></h3>
<code-well-header>
  <ExampleProfileCard />
</code-well-header>
<code-example-tabs
vueCode='
<dt-stack gap="200">
  <dt-stack gap="100" class="d-jc-space-between">
    <dt-stack>
      <dt-text as="h2" kind="headline" :size="500" strength="medium" density="200" class="d-fs-400">
        Katie Rodriguez
      </dt-text>
      <dt-stack direction="row" gap="75">
        <dt-text tone="positive">
          Available
        </dt-text>
        <dt-text>&bull;</dt-text>
        <dt-text tone="tertiary">
          Working from coffee shop
        </dt-text>
      </dt-stack>
    </dt-stack>
    <dt-stack gap="25">
      <dt-text kind="body" :size="300" density="200" strength="semibold" tone="tertiary">
        Chief Customer Success Officer
      </dt-text>
      <dt-text kind="body" :size="200" density="200">
        <dt-text strength="semibold">
          6:19 am
        </dt-text> local time
      </dt-text>
    </dt-stack>
  </dt-stack>
  <dt-stack gap="100" direction="row" class="d-jc-space-between">
    <dt-button class="d-fl1" kind="muted" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon-phone :size="iconSize" />
      </template>
      Call
    </dt-button>
    <dt-button class="d-fl1" kind="muted" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon-quick-reply :size="iconSize" />
      </template>
      Message
    </dt-button>
    <dt-button class="d-fl1" kind="muted" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon-video :size="iconSize" />
      </template>
      Meet
    </dt-button>
  </dt-stack>
</dt-stack>
'
/>
<h3 id="hero" tabindex="-1" class="d-docsite--header-3">Hero <a class="header-anchor d-link d-docsite--link d-link" href="#hero" aria-hidden="true">#</a></h3>
<code-well-header class="d-pbe-400">
  <dt-stack gap="200">
    <dt-stack>
      <dt-text as="h2" kind="headline" :size="600">AI that works for you</dt-text>
      <dt-text kind="body" :size="400">Support customers, drive sales, and collaborate with your team—all in one, beautiful AI-powered app.</dt-text>
    </dt-stack>
    <dt-stack :direction="{ 'default': 'column', 'md': 'row' }" gap="200" align="start">
      <dt-stack>
        <dt-text as="h3" kind="headline" :size="500">AI Contact Center</dt-text>
        <dt-text as="p" kind="body" :size="300">The world’s most advanced customer engagement platform</dt-text>
      </dt-stack>
      <dt-stack>
        <dt-text as="h3" kind="headline" :size="500">AI Voice</dt-text>
        <dt-text as="p" kind="body" :size="300">Say hello to the world’s smartest business phone</dt-text>
      </dt-stack>
      <dt-stack>
        <dt-text as="h3" kind="headline" :size="500">AI Meetings</dt-text>
        <dt-text as="p" kind="body" :size="300">I-powered video meetings with built-in transcriptions</dt-text>
      </dt-stack>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
vueCode='
<dt-stack gap="200">
  <dt-stack>
    <dt-text as="h2" kind="headline" :size="600">AI that works for you</dt-text>
    <dt-text kind="body" :size="400">Support customers, drive sales, and collaborate with your team—all in one, beautiful AI-powered app.</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="200" align="start">
    <dt-stack>
      <dt-text as="h3" kind="headline" :size="500">AI Contact Center</dt-text>
      <dt-text as="p" kind="body" :size="300">The world’s most advanced customer engagement platform</dt-text>
    </dt-stack>
    <dt-stack>
      <dt-text as="h3" kind="headline" :size="500">AI Voice</dt-text>
      <dt-text as="p" kind="body" :size="300">Say hello to the world’s smartest business phone</dt-text>
    </dt-stack>
    <dt-stack>
      <dt-text as="h3" kind="headline" :size="500">AI Meetings</dt-text>
      <dt-text as="p" kind="body" :size="300">AI-powered video meetings with built-in transcriptions</dt-text>
    </dt-stack>
  </dt-stack>
</dt-stack>
'
/>
<h2 id="api" tabindex="-1" class="d-docsite--header-2">API <a class="header-anchor d-link d-docsite--link d-link" href="#api" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Each typography style is expressed through a shorthand <code v-pre>font</code> property, and its value's design token contains all font styles, e.g. <code v-pre>font-size</code>, <code v-pre>line-height</code>, <code v-pre>font-family</code>, etc,</p>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-w40p  d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ var: varName, output } in typographyStyles">
          <td class="d-code--sm d-docsite-code">.{{ varName }}</td>
          <td class="d-code--sm">{{ output }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { typographyVariants, typographyStyles, weight } from '@data/type.json';
  import CopyButton from '@baseComponents/CopyButton.vue';
  import SvgLoader from '@baseComponents/SvgLoader.vue';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
  import ExampleProfileCard from '@exampleComponents/ExampleProfileCard.vue';

  const typographyStylesHeadlines = typographyStyles.filter(type => type.var.startsWith("d-text-headline"));
  const typographyStylesBody = typographyStyles.filter(type => type.var.startsWith("d-text-body"));
  const typographyStylesLabel = typographyStyles.filter(type => type.var.startsWith("d-text-label"));
  const typographyStylesCode = typographyStyles.filter(type => type.var.startsWith("d-text-code"));

  const example = "The quick brown fox jumps over the lazy dog.";

  // Map numeric font-weight to its name (e.g., "700" → "bold")
  const getWeightName = (numericWeight) => {
    const match = weight.find(w => w.output === numericWeight);
    return match ? match.name : numericWeight;
  };

  // Function to get styles directly from CSS variables
  const getStyles = (category, index) => {
    const arrays = { headlines: typographyStylesHeadlines, body: typographyStylesBody, label: typographyStylesLabel, code: typographyStylesCode };
    const item = arrays[category]?.[index];
    if (!item) return { fontSize: '', lineHeight: '', fontWeight: '', fontWeightName: '' };

    // Convert class name to CSS variable prefix
    // d-text-headline--3xl → --dt-text-headline-3xl
    const cssVarPrefix = '--dt-' + item.var.slice(2).replace('--', '-');

    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    // Read the CSS variable values directly
    const fontSize = computedStyle.getPropertyValue(cssVarPrefix + '-font-size').trim();
    const lineHeight = computedStyle.getPropertyValue(cssVarPrefix + '-line-height').trim();
    const fontWeight = computedStyle.getPropertyValue(cssVarPrefix + '-font-weight').trim();

    // Parse font-size: extract multiplier from calc(1rem * X) and convert to px
    let formattedFontSize = fontSize;
    const calcMatch = fontSize.match(/calc\(1rem \* ([\d.]+)\)/);
    if (calcMatch) {
      formattedFontSize = Math.round(parseFloat(calcMatch[1]) * 10);
    } else if (fontSize.endsWith('px')) {
      formattedFontSize = Math.round(parseFloat(fontSize));
    }

    return { fontSize: formattedFontSize, lineHeight, fontWeight, fontWeightName: getWeightName(fontWeight) };
  };
</script>
<style lang="less" scoped>
  .dialtone-copy-utility {
    & {
      position: relative;
      cursor: default;
    }

    &__btn {
      display: none;
      position: absolute;
      inset-inline-end: 0;
      background-color: var(--dt-color-surface-secondary);
      padding-inline-start: var(--dt-spacing-50);

      .dialtone-copy-utility:hover & {
        display: block;
      }
    }
  }
</style>